import { useRef, useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { Upload, Film } from 'lucide-react'

import { uploadNewVideo } from '../../../services/api/VideoDataService'
import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

const MAX_SIZE_BYTES = 200 * 1024 * 1024 // 200 MB onboarding cap (backend re-validates, allows more)
const MAX_DURATION_SECONDS = 60

// Maps a backend video error code (surfaced synchronously on the upload POST) to a friendly message.
const CODE_MESSAGES = {
    VIDEO_TOO_LARGE: 'That video is too large. Please choose a smaller file.',
    VIDEO_TOO_LONG: 'Videos must be 60 seconds or shorter.',
    INVALID_FILE_TYPE: "That file isn't a supported video (MP4, MOV, WebM, or AVI).",
    NO_VIDEO_STREAM: 'That file has no video track.',
    UPLOAD_IN_PROGRESS: 'An upload is already in progress. Please wait a moment.',
}
const messageFor = (err) => CODE_MESSAGES[err?.response?.data?.code] || 'Upload failed. Please try again.'

// Reads a video file's duration without uploading it.
function getVideoDuration(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const el = document.createElement('video')
        el.preload = 'metadata'
        el.onloadedmetadata = () => {
            URL.revokeObjectURL(url)
            resolve(el.duration)
        }
        el.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('Could not read video'))
        }
        el.src = url
    })
}

export default function VideoUploadStep({ goNext }) {
    const inputRef = useRef(null)
    const [file, setFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState('')
    const [error, setError] = useState('')
    const [dragOver, setDragOver] = useState(false)
    const [uploading, setUploading] = useState(false)

    const handleFiles = async (files) => {
        setError('')
        const selected = files && files[0]
        if (!selected) return
        if (!selected.type.startsWith('video/')) {
            setError('Please choose a video file.')
            return
        }
        if (selected.size > MAX_SIZE_BYTES) {
            setError('That video is over 200 MB. Please choose a smaller file.')
            return
        }
        try {
            const duration = await getVideoDuration(selected)
            if (duration > MAX_DURATION_SECONDS + 0.5) {
                setError('Videos must be 60 seconds or shorter.')
                return
            }
        } catch {
            setError("Couldn't read that video. Try a different file.")
            return
        }
        setFile(selected)
        setPreviewUrl(URL.createObjectURL(selected))
    }

    const handleUpload = async () => {
        if (!file) return
        setError('')
        setUploading(true)
        const formData = new FormData()
        formData.append('file', file)
        try {
            // New async pipeline: the POST returns 202 { jobId } once the bytes are received and
            // validated; transcoding continues in the background. We don't block onboarding on it —
            // the live-preview step (which loads the real page) shows the video once it's ready.
            await uploadNewVideo(formData)
            goNext()
        } catch (err) {
            // Synchronous rejections (bad type/size/duration, upload-in-progress) come back here.
            setUploading(false)
            setError(messageFor(err))
        }
    }

    return (
        <Box>
            <Typography sx={s.title}>It's all about the video</Typography>
            <Typography sx={s.subtitle}>
                Video is what makes your page yours. We highly encourage you to upload a video, so your page can speak to your audience.
            </Typography>

            {!file ? (
                <Box
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }}
                    sx={{
                        cursor: 'pointer',
                        border: `2px dashed ${dragOver ? ACCENT : '#d8d8d8'}`,
                        backgroundColor: dragOver ? 'rgba(255,20,147,0.06)' : '#fafafa',
                        borderRadius: '20px',
                        p: 4,
                        textAlign: 'center',
                        transition: 'all .15s ease',
                    }}
                >
                    <Upload size={40} color={ACCENT} />
                    <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 700, mt: 1 }}>
                        Tap to upload or drag &amp; drop
                    </Typography>
                    <Typography sx={{ fontFamily: 'Catamaran', color: 'text.gray', fontSize: '0.85rem' }}>
                        MP4 or MOV · up to 200 MB · 60 seconds max
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center' }}>
                    <Box
                        sx={{
                            mx: 'auto',
                            width: 160,
                            height: 280,
                            borderRadius: '20px',
                            overflow: 'hidden',
                            border: '6px solid #000',
                            backgroundColor: '#000',
                        }}
                    >
                        <video src={previewUrl} muted autoPlay loop playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1, color: 'text.gray' }}>
                        <Film size={16} />
                        <Typography variant="body2" sx={{ fontFamily: 'Catamaran' }} noWrap>
                            {file.name}
                        </Typography>
                    </Box>
                    {!uploading && (
                        <Button onClick={() => { setFile(null); setPreviewUrl('') }} sx={{ ...s.skipButton, mt: 0 }}>
                            Choose a different video
                        </Button>
                    )}
                </Box>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => handleFiles(e.target.files)}
            />

            {error && (
                <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600, mt: 1.5, textAlign: 'center' }}>
                    {error}
                </Typography>
            )}

            {uploading && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ fontFamily: 'Catamaran', color: 'text.gray' }}>
                        Uploading…
                    </Typography>
                </Box>
            )}

            <Box sx={s.footer}>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!file || uploading}
                    onClick={handleUpload}
                    sx={s.ctaButton}
                >
                    Upload &amp; continue
                </Button>
                <Button onClick={goNext} disabled={uploading} sx={s.skipButton}>
                    I'll add a video later
                </Button>
            </Box>
        </Box>
    )
}
